/**
 * useShopActions - Hook für Shop-Aktionen
 *
 * Extrahiert die Shop-Aktions-Logik aus App.js für bessere Wartbarkeit.
 */

import { useCallback } from 'react';
import socketService from '../socketService';
import soundService from '../soundService';

/**
 * @param {Object} config - Konfiguration mit State und Setters
 */
export function useShopActions(config) {
  const {
    // State
    gc,

    // Setters
    flashMessage, // New: flashMessage from useStatusMessage hook
  } = config;

  /**
   * Shop bereit melden
   */
  const handleShopReady = useCallback(() => {
    socketService.shopReady();
    if (flashMessage) flashMessage('Warte auf andere Spieler...', 'info', 2000);
  }, [flashMessage]);

  /**
   * Joker kaufen
   */
  const handleBuyJoker = useCallback((jokerId, forPlayerIndex = null) => {
    socketService.buyJoker(jokerId, forPlayerIndex);
    if (flashMessage) flashMessage('Joker gekauft!', 'action', 2000);
  }, [flashMessage]);

  /**
   * Joker verkaufen
   */
  const handleSellJoker = useCallback((jokerId, forPlayerIndex = null) => {
    socketService.sellJoker(jokerId, forPlayerIndex);
    if (flashMessage) flashMessage('Joker verkauft!', 'action', 2000);
  }, [flashMessage]);

  /**
   * Siegel-Item kaufen (einzelnes Siegel oder Booster)
   */
  const handleBuySealItem = useCallback((itemIndex, selectedSealId = null, forPlayerIndex = null) => {
    socketService.buySealItem(itemIndex, selectedSealId, forPlayerIndex);
  }, []);

  /**
   * Shop-Items (Siegel) neu würfeln
   */
  const handleRerollShopItems = useCallback((forPlayerIndex = null) => {
    soundService.play('shop_reroll');
    socketService.rerollShopItems(forPlayerIndex);
  }, []);

  /**
   * Shop-Joker neu würfeln
   */
  const handleRerollJokers = useCallback((forPlayerIndex = null) => {
    soundService.play('shop_reroll');
    socketService.rerollJokers(forPlayerIndex);
  }, []);

  /**
   * Seal Bundle kaufen (V3.0) - selectedSeals is array of seal objects
   */
  const handleBuyBundle = useCallback((selectedSeals, forPlayerIndex = null) => {
    socketService.buyBundle(selectedSeals, forPlayerIndex);
    const sealCount = selectedSeals?.length || 0;
    if (flashMessage) flashMessage(`${sealCount} Siegel zum Stash hinzugefügt!`, 'action', 2000);
  }, [flashMessage]);

  /**
   * Joker Bundle kaufen (V3.0) - selectedJokerId is the ID of the chosen joker
   */
  const handleBuyJokerBundle = useCallback((selectedJokerId, forPlayerIndex = null) => {
    socketService.buyJokerBundle(selectedJokerId, forPlayerIndex);
    if (flashMessage) flashMessage('Joker aus Bundle erhalten!', 'action', 2000);
  }, [flashMessage]);

  /**
   * Siegel aus Stash verkaufen (V3.0)
   */
  const handleSellSeal = useCallback((sealId, slotIndex, forPlayerIndex = null) => {
    soundService.play('shop_sell');
    socketService.sellSeal(sealId, slotIndex, forPlayerIndex);
    if (flashMessage) flashMessage('Siegel für 1 Münze verkauft!', 'action', 2000);
  }, [flashMessage]);

  /**
   * Rundenende fortfahren (zum Shop)
   */
  const handleRoundEndContinue = useCallback(() => {
    // Proceed to shop (only after round 1)
    if (gc.roundNumber >= 1) {
      socketService.proceedToShop();
    }
    return true; // Signal to close round end splash
  }, [gc.roundNumber]);

  return {
    handleShopReady,
    handleBuyJoker,
    handleSellJoker,
    handleBuySealItem,
    handleSellSeal,
    handleBuyBundle,
    handleBuyJokerBundle,
    handleRerollShopItems,
    handleRerollJokers,
    handleRoundEndContinue,
  };
}

export default useShopActions;
